import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, Grid, Alert, Snackbar,
  CircularProgress, Skeleton, InputAdornment, Chip, Tooltip, Autocomplete,
  Divider,
} from '@mui/material';
import { Search as SearchIcon, Eye, RefreshCw, FlaskConical, Plus, Trash2, Download, Upload } from 'lucide-react';
import * as XLSX from 'xlsx';
import {
  getDecantTransfers,
  createDecantTransfer,
  generateDecantTransferNumber,
} from '../firebase/decantTransferService';
import { getInventoryItems, addInventoryItem } from '../firebase/inventoryService';
import { logActivity, ACTIONS, ENTITY_TYPES } from '../firebase/activityLogService';

const fmt = (n) =>
  (n || 0).toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const noSpinner = {
  '& input[type=number]': { MozAppearance: 'textfield' },
  '& input[type=number]::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 },
  '& input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
};

const getEmptyLine = () => ({
  id: Date.now(),
  sourceItem: null,
  sourceQty: 1,
  sourceBottleML: 100,
  outputItemName: '',
  outputItemCode: '',
  outputItemId: null,
  outputCategory: 'Decants',
  costPerML: '',
  price5ml: '',
  price10ml: '',
});

const DecantTransfer = ({ currentUser }) => {
  const [transfers, setTransfers] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [viewTransfer, setViewTransfer] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState([getEmptyLine()]);

  // Import state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importPreview, setImportPreview] = useState([]);
  const [importErrors, setImportErrors] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useCallback((input) => {
    if (input) window.fileInputRef = input;
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    const [tResult, invResult] = await Promise.all([
      getDecantTransfers(),
      getInventoryItems(),
    ]);
    if (tResult.success) setTransfers(tResult.data);
    if (invResult.success) setInventory(invResult.data);
    setLoading(false);
  }, []);

  // ── Import Opening Decant Stock ──────────────────────────────────────────

  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      ['Product Name *', 'Category', 'Item Code', 'ML Remaining *', 'Cost per ML *', '5ml Retail Price *', '5ml Wholesale Price *', '10ml Retail Price *', '10ml Wholesale Price *'],
      ['Yara EDP (Decant)', 'Decants', 'DEC-001', 85, 58.36, 150, 120, 240, 200],
      ['Rose Oud (Decant)', 'Decants', 'DEC-002', 120, 45.83, 120, 100, 200, 180],
    ]);
    ws['!cols'] = [
      { wch: 30 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 },
      { wch: 20 }, { wch: 22 }, { wch: 20 }, { wch: 22 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Opening Stock');
    XLSX.writeFile(wb, 'Decant_Opening_Stock_Template.xlsx');
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

        if (rows.length < 2) {
          showSnackbar('File is empty or has no data rows', 'error');
          return;
        }

        const headers = rows[0].map(h => String(h).trim().toLowerCase());
        const col = (names) => {
          for (const n of names) {
            const idx = headers.findIndex(h => h.includes(n));
            if (idx !== -1) return idx;
          }
          return -1;
        };

        const nameCol = col(['product name', 'name', 'product']);
        const categoryCol = col(['category', 'cat']);
        const itemCodeCol = col(['item code', 'itemcode', 'code']);
        const mlCol = col(['ml remaining', 'ml', 'stock']);
        const costPerMLCol = col(['cost per ml', 'cost']);
        const price5mlRetailCol = col(['5ml retail', '5ml retail price']);
        const price5mlWholesaleCol = col(['5ml wholesale', '5ml wholesale price']);
        const price10mlRetailCol = col(['10ml retail', '10ml retail price']);
        const price10mlWholesaleCol = col(['10ml wholesale', '10ml wholesale price']);

        if (nameCol === -1 || mlCol === -1 || costPerMLCol === -1 || price5mlRetailCol === -1 || price10mlRetailCol === -1) {
          showSnackbar('Required columns not found. Download the template to see the correct format.', 'error');
          return;
        }

        const preview = [];
        const errors = [];

        rows.slice(1).forEach((row, i) => {
          const rowNum = i + 2;
          const name = String(row[nameCol] || '').trim();
          const category = categoryCol !== -1 ? String(row[categoryCol] || '').trim() : 'Decants';
          const itemCode = itemCodeCol !== -1 ? String(row[itemCodeCol] || '').trim() : '';
          const ml = parseFloat(row[mlCol]);
          const costPerML = parseFloat(row[costPerMLCol]);
          const price5mlRetail = parseFloat(row[price5mlRetailCol]);
          const price5mlWholesale = price5mlWholesaleCol !== -1 ? parseFloat(row[price5mlWholesaleCol]) : price5mlRetail;
          const price10mlRetail = parseFloat(row[price10mlRetailCol]);
          const price10mlWholesale = price10mlWholesaleCol !== -1 ? parseFloat(row[price10mlWholesaleCol]) : price10mlRetail;

          if (!name && isNaN(ml) && isNaN(costPerML)) return; // skip blank rows

          if (!name) {
            errors.push(`Row ${rowNum}: Product name is empty`);
            return;
          }
          if (isNaN(ml) || ml < 0) {
            errors.push(`Row ${rowNum}: Invalid ML remaining "${row[mlCol]}"`);
            return;
          }
          if (isNaN(costPerML) || costPerML < 0) {
            errors.push(`Row ${rowNum}: Invalid cost per ML "${row[costPerMLCol]}"`);
            return;
          }
          if (isNaN(price5mlRetail) || price5mlRetail < 0) {
            errors.push(`Row ${rowNum}: Invalid 5ml retail price "${row[price5mlRetailCol]}"`);
            return;
          }
          if (isNaN(price10mlRetail) || price10mlRetail < 0) {
            errors.push(`Row ${rowNum}: Invalid 10ml retail price "${row[price10mlRetailCol]}"`);
            return;
          }

          // Check if already exists in current inventory
          const exists = inventory.some(
            inv => inv.name.toLowerCase() === name.toLowerCase() && inv.isDecant
          );

          preview.push({
            name,
            category: category || 'Decants',
            itemCode,
            ml,
            costPerML,
            price5mlRetail,
            price5mlWholesale: isNaN(price5mlWholesale) ? price5mlRetail : price5mlWholesale,
            price10mlRetail,
            price10mlWholesale: isNaN(price10mlWholesale) ? price10mlRetail : price10mlWholesale,
            exists,
          });
        });

        setImportPreview(preview);
        setImportErrors(errors);
        setImportResult(null);
        setShowImportDialog(true);
      } catch (err) {
        showSnackbar('Failed to read file. Make sure it is a valid Excel or CSV file.', 'error');
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleConfirmImport = async () => {
    const toImport = importPreview.filter(row => !row.exists);
    if (toImport.length === 0) {
      showSnackbar('No new products to import — all rows already exist in inventory', 'info');
      return;
    }

    setImporting(true);

    let created = 0;
    let failed = 0;
    const failedNames = [];

    for (let i = 0; i < toImport.length; i++) {
      const row = toImport[i];

      const itemData = {
        name: row.name,
        category: row.category,
        itemCode: row.itemCode || '',
        stock: row.ml,
        costPrice: row.costPerML,
        averageCost: row.costPerML,
        retailPrice: row.price5mlRetail,
        wholesalePrice: row.price5mlWholesale,
        sellingPrice: row.price5mlRetail,
        isDecant: true,
        trackInML: true,
        decantPrices: {
          5: { retail: row.price5mlRetail, wholesale: row.price5mlWholesale },
          10: { retail: row.price10mlRetail, wholesale: row.price10mlWholesale },
        },
        batches: [{
          batchId: `opening-decant-${Date.now()}-${i}`,
          purchaseInvoiceId: 'opening-stock',
          purchaseInvoiceNumber: 'OPENING',
          quantity: row.ml,
          costPerUnit: row.costPerML,
          dateReceived: new Date().toISOString().split('T')[0],
        }],
      };

      const result = await addInventoryItem(itemData);
      if (result.success) {
        created++;
      } else {
        failed++;
        failedNames.push(row.name);
      }
    }

    setImporting(false);
    setImportResult({ created, failed, failedNames });

    if (failed === 0) {
      showSnackbar(`✓ ${created} decant products imported successfully`);
      const invResult = await getInventoryItems();
      if (invResult.success) setInventory(invResult.data);

      logActivity({
        userId: currentUser?.uid,
        userEmail: currentUser?.email,
        userName: currentUser?.displayName || currentUser?.email,
        action: ACTIONS.CREATED_INVENTORY,
        entityType: ENTITY_TYPES.INVENTORY,
        entityId: 'bulk-import',
        entityName: 'Opening Decant Stock Import',
        details: { itemsImported: created },
      });

      setTimeout(() => setShowImportDialog(false), 1500);
    } else {
      showSnackbar(`${created} imported, ${failed} failed`, 'warning');
    }
  };

  // ──────────────────────────────────────────────────────────────────────────

  useEffect(() => { loadData(); }, [loadData]);

  const showSnackbar = (msg, sev = 'success') => setSnackbar({ open: true, message: msg, severity: sev });

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
    showSnackbar('Refreshed');
  };

  // Source: perfume bottles only (not decants, not internal)
  const sourceItems = inventory.filter((i) =>
    i.stock > 0 &&
    !i.isInternal &&
    (i.category || '').toLowerCase() !== 'decants'
  );

  const openCreate = () => {
    setDate(new Date().toISOString().split('T')[0]);
    setNotes('');
    setLines([getEmptyLine()]);
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setLines([]); };

  const addLine = () => setLines((prev) => [...prev, { ...getEmptyLine(), id: Date.now() }]);
  const removeLine = (id) => setLines((prev) => prev.filter((l) => l.id !== id));

  const updateLine = (id, field, value) => {
    setLines((prev) => prev.map((line) => {
      if (line.id !== id) return line;
      const updated = { ...line, [field]: value };

      if (field === 'sourceItem' && value) {
        // Auto-fill output name (strip ML from name)
        const baseName = value.name.replace(/\s*\d+\s*ml\s*/gi, '').trim();
        updated.outputItemName = `${baseName} (Decant)`;
        // Auto-calculate cost per ML
        const costPerML = (value.costPrice || 0) / (updated.sourceBottleML || 100);
        updated.costPerML = costPerML.toFixed(4);
        // Check if output item already exists
        const existing = inventory.find(
          (i) => i.name.toLowerCase() === updated.outputItemName.toLowerCase()
        );
        updated.outputItemId = existing?.id || null;
        if (existing?.decantPrices) {
          updated.price5ml = existing.decantPrices[5] || '';
          updated.price10ml = existing.decantPrices[10] || '';
        }
      }

      if (field === 'sourceBottleML' && updated.sourceItem) {
        const costPerML = (updated.sourceItem.costPrice || 0) / (parseFloat(value) || 100);
        updated.costPerML = costPerML.toFixed(4);
      }

      if (field === 'sourceQty' && updated.sourceItem) {
        updated.sourceQty = Math.min(parseFloat(value) || 0, updated.sourceItem.stock);
      }

      return updated;
    }));
  };

  const totalML = (line) =>
    parseFloat(line.sourceQty || 0) * parseFloat(line.sourceBottleML || 0);

  const handleCreate = async () => {
    if (submitting) return;

    for (const line of lines) {
      if (!line.sourceItem) { showSnackbar('Select a source perfume for all lines', 'error'); return; }
      if (totalML(line) <= 0) { showSnackbar('Total ML must be greater than 0', 'error'); return; }
      if (!line.outputItemName.trim()) { showSnackbar('Enter output product name', 'error'); return; }
      if (!line.price5ml && !line.price10ml) { showSnackbar('Enter at least one decant price (5ml or 10ml)', 'error'); return; }
      if (parseFloat(line.sourceQty) > line.sourceItem.stock) {
        showSnackbar(`Insufficient stock for "${line.sourceItem.name}"`, 'error'); return;
      }
    }

    setSubmitting(true);
    try {
      const { transferNumber } = await generateDecantTransferNumber();

      const result = await createDecantTransfer({
        transferNumber,
        date,
        notes,
        createdBy: currentUser?.email || '',
        lines: lines.map((line) => ({
          sourceItemId: line.sourceItem.id,
          sourceItemName: line.sourceItem.name,
          sourceQty: parseFloat(line.sourceQty),
          sourceBottleML: parseFloat(line.sourceBottleML),
          totalML: totalML(line),
          outputItemName: line.outputItemName.trim(),
          outputItemCode: line.outputItemCode?.trim() || '',
          outputItemId: line.outputItemId || null,
          outputCategory: line.outputCategory || 'Decants',
          costPerML: parseFloat(line.costPerML) || 0,
          price5ml: parseFloat(line.price5ml) || 0,
          price5mlWholesale: parseFloat(line.price5mlWholesale) || parseFloat(line.price5ml) || 0,
          price10ml: parseFloat(line.price10ml) || 0,
          price10mlWholesale: parseFloat(line.price10mlWholesale) || parseFloat(line.price10ml) || 0,
        })),
      });

      if (result.success) {
        setTransfers((prev) => [{
          id: result.id,
          transferNumber: result.transferNumber,
          date, notes,
          lines: result.processedLines,
          totalML: result.totalML,
          createdAt: new Date().toISOString(),
        }, ...prev]);

        const invResult = await getInventoryItems();
        if (invResult.success) setInventory(invResult.data);

        closeModal();
        showSnackbar(`${result.transferNumber} completed — ${result.totalML}ml added to inventory`);

        logActivity({
          userId: currentUser?.uid,
          userEmail: currentUser?.email,
          userName: currentUser?.displayName || currentUser?.email,
          action: ACTIONS.CREATED_STOCK_TRANSFER,
          entityType: ENTITY_TYPES.STOCK_TRANSFER,
          entityId: result.id,
          entityName: `Decant Transfer #${result.transferNumber}`,
          details: { totalML: result.totalML, lines: lines.length },
        });
      } else {
        showSnackbar(result.error || 'Decant transfer failed', 'error');
      }
    } catch (err) {
      showSnackbar('Unexpected error during decant transfer', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const filtered = transfers.filter(
    (t) =>
      (t.transferNumber || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.notes || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width={300} height={50} sx={{ mb: 1 }} />
        <Skeleton variant="rectangular" width="100%" height={400} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>Decant Transfer</Typography>
        <Typography variant="body1" color="text.secondary">
          Convert perfume bottles into ML-tracked decant products. Set 5ml and 10ml prices for billing.
        </Typography>
      </Box>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h6" fontWeight={600}>All Decant Transfers</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="outlined" size="small"
                startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshCw size={16} />}
                onClick={handleRefresh} disabled={refreshing}>
                Refresh
              </Button>
              <Button variant="outlined" size="small" startIcon={<Download size={16} />} onClick={() => setShowImportDialog(true)}>
                Import Opening Stock
              </Button>
              <Button variant="contained" size="small" startIcon={<FlaskConical size={16} />} onClick={openCreate}>
                New Decant Transfer
              </Button>
            </Box>
          </Box>

          <TextField fullWidth placeholder="Search by transfer # or notes..."
            value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} sx={{ mb: 3 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon size={18} /></InputAdornment> }}
          />

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Transfer #</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>Lines</strong></TableCell>
                  <TableCell align="right"><strong>Total ML</strong></TableCell>
                  <TableCell><strong>Created By</strong></TableCell>
                  <TableCell><strong>Notes</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((t) => (
                  <TableRow key={t.id} hover>
                    <TableCell>
                      <Typography variant="body2" color="primary" fontWeight={600}>{t.transferNumber}</Typography>
                    </TableCell>
                    <TableCell>{t.date}</TableCell>
                    <TableCell>{t.lines?.length || 0}</TableCell>
                    <TableCell align="right">
                      <Chip label={`${t.totalML} ml`} size="small" color="success" />
                    </TableCell>
                    <TableCell><Typography variant="caption">{t.createdBy}</Typography></TableCell>
                    <TableCell><Typography variant="caption" color="text.secondary">{t.notes || '—'}</Typography></TableCell>
                    <TableCell align="center">
                      <Tooltip title="View details">
                        <IconButton size="small" color="primary" onClick={() => setViewTransfer(t)}>
                          <Eye size={17} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                      <FlaskConical size={40} style={{ opacity: 0.3, marginBottom: 8 }} />
                      <Typography color="text.secondary">No decant transfers found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showModal} onClose={closeModal} maxWidth="lg" fullWidth>
        <DialogTitle>New Decant Transfer</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Date" type="date" value={date}
                  onChange={(e) => setDate(e.target.value)} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField fullWidth label="Notes" value={notes}
                  onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes" />
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={600}>Decant Lines</Typography>
              <Button size="small" startIcon={<Plus size={15} />} onClick={addLine} variant="outlined">
                Add Line
              </Button>
            </Box>

            {lines.map((line, idx) => (
              <Card key={line.id} variant="outlined" sx={{ mb: 2, p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600} color="primary">Line {idx + 1}</Typography>
                  {lines.length > 1 && (
                    <IconButton size="small" color="error" onClick={() => removeLine(line.id)}>
                      <Trash2 size={15} />
                    </IconButton>
                  )}
                </Box>

                {/* Source */}
                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
                  Source (Perfume Bottle)
                </Typography>
                <Grid container spacing={2} sx={{ mt: 0.5, mb: 2 }}>
                  <Grid item xs={12} sm={4}>
                    <Autocomplete
                      options={sourceItems}
                      getOptionLabel={(i) => `${i.name} (${i.stock} units)`}
                      value={line.sourceItem}
                      onChange={(_, val) => updateLine(line.id, 'sourceItem', val)}
                      renderInput={(params) => (
                        <TextField {...params} label="Perfume Bottle *" size="small"
                          placeholder="Select perfume to decant" />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField fullWidth label="Bottles to Use *" type="number" size="small"
                      value={line.sourceQty}
                      onChange={(e) => {
                        const max = line.sourceItem?.stock || 9999;
                        const val = Math.min(parseFloat(e.target.value) || 0, max);
                        updateLine(line.id, 'sourceQty', val);
                      }}
                      inputProps={{ min: 1, step: 1, max: line.sourceItem?.stock }}
                      error={line.sourceItem && parseFloat(line.sourceQty) > line.sourceItem.stock}
                      helperText={line.sourceItem ? `Max: ${line.sourceItem.stock}` : ''}
                      sx={noSpinner}
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField fullWidth label="Bottle Size (ML) *" type="number" size="small"
                      value={line.sourceBottleML}
                      onChange={(e) => updateLine(line.id, 'sourceBottleML', e.target.value)}
                      inputProps={{ min: 1 }}
                      helperText="e.g. 100 for 100ml"
                      sx={noSpinner}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Alert severity="info" sx={{ py: 0.5 }}>
                      Total ML to add: <strong>{totalML(line)} ml</strong>
                    </Alert>
                  </Grid>
                </Grid>

                {/* Output */}
                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
                  Output (Decant Product)
                </Typography>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  <Grid item xs={12} sm={3}>
                    <TextField fullWidth label="Product Name *" size="small"
                      value={line.outputItemName}
                      onChange={(e) => updateLine(line.id, 'outputItemName', e.target.value)}
                      helperText={line.outputItemId ? '✓ Existing — ML will be added to stock' : 'New product will be created'}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField fullWidth label="Item Code" size="small"
                      value={line.outputItemCode || ''}
                      onChange={(e) => updateLine(line.id, 'outputItemCode', e.target.value)}
                      helperText="e.g. DEC05ML110"
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField fullWidth label="Cost per ML (LKR)" type="number" size="small"
                      value={line.costPerML}
                      onChange={(e) => updateLine(line.id, 'costPerML', e.target.value)}
                      inputProps={{ min: 0, step: 0.0001 }}
                      helperText="Auto-calculated"
                      sx={noSpinner}
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField fullWidth label="5ml Retail (LKR) *" type="number" size="small"
                      value={line.price5ml}
                      onChange={(e) => updateLine(line.id, 'price5ml', e.target.value)}
                      inputProps={{ min: 0, step: 0.01 }}
                      sx={noSpinner}
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField fullWidth label="5ml Wholesale (LKR)" type="number" size="small"
                      value={line.price5mlWholesale || ''}
                      onChange={(e) => updateLine(line.id, 'price5mlWholesale', e.target.value)}
                      inputProps={{ min: 0, step: 0.01 }}
                      sx={noSpinner}
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField fullWidth label="10ml Retail (LKR) *" type="number" size="small"
                      value={line.price10ml}
                      onChange={(e) => updateLine(line.id, 'price10ml', e.target.value)}
                      inputProps={{ min: 0, step: 0.01 }}
                      sx={noSpinner}
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField fullWidth label="10ml Wholesale (LKR)" type="number" size="small"
                      value={line.price10mlWholesale || ''}
                      onChange={(e) => updateLine(line.id, 'price10mlWholesale', e.target.value)}
                      inputProps={{ min: 0, step: 0.01 }}
                      sx={noSpinner}
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField fullWidth label="Category" size="small"
                      value={line.outputCategory}
                      onChange={(e) => updateLine(line.id, 'outputCategory', e.target.value)}
                    />
                  </Grid>
                </Grid>
              </Card>
            ))}

            <Alert severity="info" sx={{ mt: 2 }}>
              Source perfume bottles will be deducted. The decant product stock is tracked in ML.
              When billing, cashier selects 5ml or 10ml and the correct ML is deducted automatically.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeModal} disabled={submitting}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={submitting || lines.length === 0}
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <FlaskConical size={16} />}>
            {submitting ? 'Processing...' : 'Confirm Decant Transfer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={!!viewTransfer} onClose={() => setViewTransfer(null)} maxWidth="md" fullWidth>
        <DialogTitle>Decant Transfer — {viewTransfer?.transferNumber}</DialogTitle>
        <DialogContent>
          {viewTransfer && (
            <Box sx={{ pt: 1 }}>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Transfer #</Typography>
                  <Typography fontWeight={600}>{viewTransfer.transferNumber}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Date</Typography>
                  <Typography fontWeight={600}>{viewTransfer.date}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Total ML Added</Typography>
                  <Chip label={`${viewTransfer.totalML} ml`} color="success" size="small" />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Created By</Typography>
                  <Typography variant="caption">{viewTransfer.createdBy}</Typography>
                </Grid>
              </Grid>
              {viewTransfer.notes && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Notes: {viewTransfer.notes}
                </Typography>
              )}
              <Divider sx={{ mb: 2 }} />
              {viewTransfer.lines?.map((line, i) => (
                <Card key={i} variant="outlined" sx={{ mb: 2, p: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary">Source</Typography>
                      <Typography variant="body2" fontWeight={600}>{line.sourceItemName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {line.sourceQty} bottle(s) × {line.sourceBottleML}ml = {line.totalML}ml
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary">Output Product</Typography>
                      <Typography variant="body2" fontWeight={600}>{line.outputItemName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Cost/ml: LKR {fmt(line.costPerML)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary">Decant Prices</Typography>
                      <Typography variant="body2">5ml: LKR {fmt(line.price5ml)}</Typography>
                      <Typography variant="body2">10ml: LKR {fmt(line.price10ml)}</Typography>
                    </Grid>
                  </Grid>
                </Card>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setViewTransfer(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Import Opening Stock Dialog */}
      <Dialog open={showImportDialog} onClose={() => !importing && setShowImportDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Import Opening Decant Stock</DialogTitle>
        <DialogContent>
          {importResult ? (
            <Box sx={{ pt: 2, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                {importResult.failed === 0 ? '✓ Import Complete' : 'Import Completed with Issues'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {importResult.created} items imported
                {importResult.failed > 0 && `, ${importResult.failed} failed`}
              </Typography>
              {importResult.failed > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Failed items: {importResult.failedNames.join(', ')}
                </Alert>
              )}
            </Box>
          ) : importPreview.length > 0 ? (
            <Box sx={{ pt: 2 }}>
              {importErrors.length > 0 && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight={600} gutterBottom>
                    {importErrors.length} error(s) found:
                  </Typography>
                  {importErrors.map((err, i) => (
                    <Typography key={i} variant="caption" display="block">
                      • {err}
                    </Typography>
                  ))}
                </Alert>
              )}

              <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ mt: 2 }}>
                Preview ({importPreview.filter(r => !r.exists).length} new, {importPreview.filter(r => r.exists).length} existing)
              </Typography>

              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, overflow: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'action.hover' }}>
                      <TableCell><strong>Product Name</strong></TableCell>
                      <TableCell><strong>Category</strong></TableCell>
                      <TableCell align="right"><strong>ML</strong></TableCell>
                      <TableCell align="right"><strong>Cost/ML</strong></TableCell>
                      <TableCell align="right"><strong>5ml Retail</strong></TableCell>
                      <TableCell align="right"><strong>10ml Retail</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {importPreview.map((row, i) => (
                      <TableRow key={i} sx={{ opacity: row.exists ? 0.5 : 1 }}>
                        <TableCell>{row.name}</TableCell>
                        <TableCell>{row.category}</TableCell>
                        <TableCell align="right">{row.ml}</TableCell>
                        <TableCell align="right">LKR {fmt(row.costPerML)}</TableCell>
                        <TableCell align="right">LKR {fmt(row.price5mlRetail)}</TableCell>
                        <TableCell align="right">LKR {fmt(row.price10mlRetail)}</TableCell>
                        <TableCell>
                          <Chip
                            label={row.exists ? 'Exists' : 'New'}
                            size="small"
                            color={row.exists ? 'default' : 'success'}
                            variant={row.exists ? 'outlined' : 'filled'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Alert severity="info" sx={{ mt: 2 }}>
                {importPreview.filter(r => r.exists).length > 0 && (
                  <>
                    <Typography variant="body2" fontWeight={600}>
                      {importPreview.filter(r => r.exists).length} product(s) already exist and will be skipped.
                    </Typography>
                  </>
                )}
                {importPreview.filter(r => !r.exists).length > 0 && (
                  <Typography variant="body2">
                    {importPreview.filter(r => !r.exists).length} new product(s) will be added to inventory.
                  </Typography>
                )}
              </Alert>
            </Box>
          ) : (
            <Box sx={{ pt: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Download the template, fill in your opening decant stock, and upload the file.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button variant="outlined" startIcon={<Download size={18} />} onClick={downloadTemplate}>
                  Download Template
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Upload size={18} />}
                  onClick={() => window.fileInputRef?.click()}
                >
                  Upload File
                </Button>
              </Box>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                style={{ display: 'none' }}
                onChange={handleFileUpload}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {importPreview.length > 0 && !importResult && (
            <>
              <Button onClick={() => { setImportPreview([]); setImportErrors([]); }} disabled={importing}>
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirmImport}
                disabled={importing || importPreview.filter(r => !r.exists).length === 0}
                startIcon={importing ? <CircularProgress size={18} color="inherit" /> : <Upload size={16} />}
              >
                {importing ? 'Importing...' : 'Confirm Import'}
              </Button>
            </>
          )}
          {(importResult || importPreview.length === 0) && (
            <Button onClick={() => { setShowImportDialog(false); setImportPreview([]); setImportErrors([]); setImportResult(null); }}>
              {importResult ? 'Close' : 'Cancel'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DecantTransfer;
